// Pure functions over the PlayerView. pickScreen decides what a phone shows; everything here is
// presentation logic only (the server has already decided what this seat may see and do).
import type { NominationView, PlayerView, SeatPublic } from "../protocol";
import type { UiState } from "./store";

export type ScreenId =
  | "lobby"
  | "role_reveal"
  | "night_sleep"
  | "night_prompt"
  | "night_info"
  | "dawn"
  | "day"
  | "nomination"
  | "vote_turn"
  | "execution"
  | "game_over";

export function pickScreen(view: PlayerView, ui: Pick<UiState, "dawnSeenDay">): ScreenId {
  switch (view.phase) {
    case "lobby":
      return "lobby";
    case "setup":
      return "role_reveal";
    case "first_night":
    case "night": {
      const prompt = view.private.prompt;
      if (!prompt) return "night_sleep";
      return prompt.kind === "choose_seats" ? "night_prompt" : "night_info";
    }
    case "day": {
      const day = view.day;
      if (!day) return "day";
      if (day.step === "execution") return "execution";
      if (day.current_nomination) {
        return view.private.can_vote_now ? "vote_turn" : "nomination";
      }
      if (isDawn(view) && ui.dawnSeenDay < day.day_number) return "dawn";
      return "day";
    }
    case "game_over":
      return "game_over";
  }
}

/** A moment this phone needs its player: it buzzes (and chimes, if sound is on). */
export type Nudge = "role" | "dawn" | "on_trial" | "vote" | "game_over" | "wake" | "died";

/**
 * What the screen asks of this player right now, or null. Role, dawn and game over reach every
 * phone at once; the trial and the vote turn are public already. At night only a woken phone
 * nudges, and only with decoys on: wakes come one phone at a time, and without decoys a buzz
 * would tell the table who has a night ability. With them every living phone wakes, though the
 * table can still hear the order (README "How a game runs"). A sleeping phone never nudges.
 */
export function nudgeFor(screen: ScreenId, view: PlayerView): Nudge | null {
  switch (screen) {
    case "night_prompt":
    case "night_info":
      // one nudge for the whole wake: a choice and then its result is still one wake
      return view.config.night_decoys ? "wake" : null;
    case "role_reveal":
      return view.private.role_acked ? null : "role";
    case "dawn":
      return "dawn";
    case "nomination": {
      const nom = view.day?.current_nomination;
      const onTrial = nom?.stage === "speeches" && nom.nominee_seat_id === view.me_seat_id;
      return onTrial ? "on_trial" : null;
    }
    case "vote_turn":
      return "vote";
    case "execution":
      // executed, and may choose a player before night falls (a Moonchild must): every
      // execution is public already. A night death was announced at dawn, which buzzed.
      return view.private.can_death_choice ? "died" : null;
    case "game_over":
      return "game_over";
    default:
      return null;
  }
}

/** The day has just begun: nothing has happened yet that the dawn screen would hide. */
export function isDawn(view: PlayerView): boolean {
  const day = view.day;
  return (
    !!day &&
    day.step === "open" &&
    !day.current_nomination &&
    day.past_nominations.length === 0 &&
    day.shots.length === 0
  );
}

export function seatMap(view: PlayerView): Record<string, SeatPublic> {
  const map: Record<string, SeatPublic> = {};
  for (const s of view.seats) map[s.seat_id] = s;
  return map;
}

export function seatName(view: PlayerView, seatId: string | null | undefined): string {
  if (!seatId) return "nobody";
  return view.seats.find((s) => s.seat_id === seatId)?.name ?? "someone";
}

export function mySeat(view: PlayerView): SeatPublic | undefined {
  return view.seats.find((s) => s.seat_id === view.me_seat_id);
}

export function isHost(view: PlayerView): boolean {
  return view.host_seat_id === view.me_seat_id;
}

export function namesList(names: string[]): string {
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

export function dawnHeadline(view: PlayerView): string {
  const deaths = view.day?.deaths_last_night ?? [];
  if (deaths.length === 0) return "Nobody died in the night.";
  const names = namesList(deaths.map((id) => seatName(view, id)));
  return `${names} died in the night.`;
}

/** Players who came back to life in the night (a Professor, a Shabaloth), or "". */
export function revivedHeadline(view: PlayerView): string {
  const back = view.day?.revived_last_night ?? [];
  if (back.length === 0) return "";
  const names = namesList(back.map((id) => seatName(view, id)));
  return `${names} ${back.length === 1 ? "is" : "are"} alive again.`;
}

export interface VoteTally {
  yes: number;
  needed: number;
  blockVotes: number;
  /** Votes still to come, including the current voter. */
  remaining: number;
}

/**
 * Where a nomination stands. The ballot's threshold is fixed when voting starts; before that
 * (during speeches) the day's current threshold is the best estimate.
 */
export function voteTally(
  nom: NominationView,
  blockVotes: number,
  dayThreshold = 0,
): VoteTally {
  const cast = nom.votes.filter((v) => v.yes !== null).length;
  const threshold = nom.stage === "speeches" ? dayThreshold : nom.threshold;
  return {
    yes: nom.yes_count,
    needed: Math.max(threshold, blockVotes + 1),
    blockVotes,
    remaining: nom.voter_order.length - cast,
  };
}

const OUTCOME_TEXT: Record<string, string> = {
  on_block: "is on the block",
  tie_cleared: "tied the block: nobody is on the block",
  below_threshold: "did not get enough votes",
  not_enough_to_replace: "did not get enough votes to replace the block",
  virgin_execution: "was executed by the Virgin",
};

export function outcomeText(view: PlayerView, nom: NominationView): string {
  const who = seatName(view, nom.nominee_seat_id);
  return nom.outcome ? `${who} ${OUTCOME_TEXT[nom.outcome] ?? nom.outcome}` : who;
}

export function formatSeconds(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return m > 0 ? `${m}:${String(s).padStart(2, "0")}` : `${s}`;
}
